import { PageHeader } from "@bednbody/ui/components/page-header";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Button } from "@bednbody/ui/components/button";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import { CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { buildQualificationIndex, cellKey, type CellState } from "./qualifications";
import {
  useGrantQualification,
  useQualifications,
  useRevokeQualification,
  useServices,
  useTherapists,
} from "./qualifications.hooks";

/**
 * Therapist × service qualification grid — DEMO-11.
 *
 * This is the screen 0037 was written for and never got, which is why the
 * table could only ever hold the dev seed's placeholders. See
 * qualifications.hooks.ts for why writes go through two audited RPCs and why
 * the route is superadmin-only.
 *
 * THE PLACEHOLDER DISTINCTION IS THE POINT OF THE UI. A placeholder row makes
 * both engines behave as if the therapist is qualified — bookings flow, screens
 * render — while being explicitly marked as fake data with no grantor. If this
 * screen drew placeholders and confirmed rows the same way, the whole roster
 * would look certified and nobody would ever notice that none of it is.
 */
export function QualificationsPage() {
  const { data: rows, isLoading, isError, refetch } = useQualifications();
  const { data: therapists } = useTherapists();
  const { data: services } = useServices();
  const grant = useGrantQualification();
  const revoke = useRevokeQualification();
  const [busy, setBusy] = useState<string | null>(null);

  const index = useMemo(() => buildQualificationIndex(rows ?? []), [rows]);

  const placeholderCount = useMemo(
    () => (rows ?? []).filter((row) => row.source !== "confirmed").length,
    [rows],
  );

  type Therapist = NonNullable<typeof therapists>[number];
  const columns = useMemo<ColumnDef<Therapist, unknown>[]>(
    () => [
      {
        id: "therapist",
        accessorFn: (therapist) => therapist.full_name,
        header: "Therapist",
        cell: ({ row }) => (
          <AdminTableIdentity
            title={row.original.full_name}
            subtitle="Active therapist"
            fallback={row.original.full_name.slice(0, 2).toUpperCase()}
          />
        ),
      },
      ...(services ?? []).map(
        (service): ColumnDef<Therapist, unknown> => ({
          id: service.id,
          accessorFn: (therapist) => index.get(cellKey(therapist.id, service.id)) ?? "none",
          header: `${service.category} · ${service.name}`,
          enableSorting: true,
          cell: ({ row }) => {
            const key = cellKey(row.original.id, service.id);
            const state: CellState = index.get(key) ?? "none";
            return (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy === key}
                onClick={() => void toggle(row.original.id, service.id, state)}
                className="w-full justify-start"
              >
                {state === "confirmed" ? (
                  <>
                    <CheckCircleIcon className="text-success" weight="fill" />
                    Confirmed
                  </>
                ) : state === "placeholder" ? (
                  <>
                    <WarningCircleIcon className="text-warning" weight="fill" />
                    Placeholder
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Button>
            );
          },
        }),
      ),
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <AdminTableRowMenu
            label={row.original.full_name}
            actions={[
              {
                label: "Copy therapist ID",
                onSelect: () => void navigator.clipboard.writeText(row.original.id),
              },
            ]}
          />
        ),
      },
    ],
    [busy, index, services],
  );

  async function toggle(staffId: string, serviceId: string, state: CellState) {
    const key = cellKey(staffId, serviceId);
    setBusy(key);
    try {
      if (state === "confirmed") {
        const result = await revoke.mutateAsync({ staffId, serviceId });
        toast.success(
          result.outcome === "revoked" ? "Qualification revoked." : "Nothing to revoke.",
        );
      } else {
        // A placeholder is confirmed through the SAME grant call: the RPC's
        // upsert flips `source` to 'confirmed' and stamps the grantor, so
        // there is no separate "promote" path that could skip the audit row.
        await grant.mutateAsync({ staffId, serviceId });
        toast.success("Qualification confirmed.");
      }
    } catch (error) {
      // A non-superadmin gets a permission error from Postgres. Show it rather
      // than pretending the click did nothing — the refusal is the control
      // working (SECURITY §9a).
      toast.error("That change was refused", {
        description: error instanceof Error ? error.message : "Superadmin access is required.",
      });
    } finally {
      setBusy(null);
    }
  }

  if (isLoading) return <Skeleton className="h-64 motion-reduce:animate-none" />;
  if (isError) {
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4">
        <p className="font-semibold">We couldn't load qualifications.</p>
        <RetryButton className="mt-3" size="sm" onRetry={() => refetch()}>
          Try again
        </RetryButton>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        description="Which services each therapist is certified to perform. An unqualified therapist is blocked by both booking engines."
        title="Therapist qualifications"
      />

      {placeholderCount > 0 ? (
        <div className="flex items-start gap-3 rounded-2xl border border-warning/40 bg-warning/5 p-4">
          <WarningCircleIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-warning" />
          <div className="text-sm">
            <p className="font-semibold">
              {placeholderCount} qualification{placeholderCount === 1 ? " is" : "s are"} seeded
              placeholder data.
            </p>
            <p className="mt-0.5 text-muted-foreground">
              They let the engines run, and every one of them is expected to be wrong. Confirming a
              cell records you as the grantor in the audit log.
            </p>
          </div>
        </div>
      ) : null}

      <AdminDataTable
        presentation="standalone"
        data={therapists ?? []}
        columns={columns}
        getRowId={(therapist) => therapist.id}
        searchText={(therapist) => therapist.full_name}
        searchPlaceholder="Search therapists…"
        initialSorting={[{ id: "therapist", desc: false }]}
        exportFilename="therapist-qualifications"
        exportColumns={[
          { header: "Therapist", value: (therapist) => therapist.full_name },
          ...(services ?? []).map((service) => ({
            header: service.name,
            value: (therapist: Therapist) => index.get(cellKey(therapist.id, service.id)) ?? "none",
          })),
        ]}
      />

      <p className="text-xs text-muted-foreground">
        Revoking is a soft delete: the row is kept with a revocation timestamp, because a
        qualification that existed when a booking was taken is part of why that booking was allowed.
        Every grant and revoke is written to the audit log.
      </p>
    </div>
  );
}
